import { BreadcrumbJsonLd, NextSeo } from 'next-seo'

import useSWR from 'swr'

import { PersonsDocument } from '@/generated/contentful'

import { client } from '@/lib/contentful/client'
import { getBaseUrl, serializeCacheKey } from '@/lib/contentful/utils'

import { getSeoLocale } from '@/utils/locales/getSeoLocale'
import { useUrl } from '@/utils/url/useUrl'

type Props = {
	children: React.ReactNode
	slug: string
	locale?: string
}

export function CompanyPersonMeta({ children, slug, locale = 'en-US' }: Props) {
	const url = useUrl({ locale })
	const baseUrl = `${url?.protocol}//${url?.host}`

	const { data: person } = useSWR(serializeCacheKey('person', { slug, locale: 'en-US' }), () =>
		client().request(PersonsDocument, {
			where: { slug },
			locale: 'en-US',
			limit: 1
		})
	)

	const { fullName, jobTitle, seoMetadata } = person?.personCollection?.items?.[0] ?? {}

	const title = seoMetadata?.title ?? `${fullName}, ${jobTitle} | BigCommerce`
	const description = seoMetadata?.description ?? ''

	return (
		<>
			<NextSeo
				title={title}
				description={description}
				canonical={seoMetadata?.canonicalUrl || `${baseUrl}/company/leaders/${slug}/`}
				languageAlternates={[
					{ hrefLang: 'en-au', href: `${getBaseUrl('en-AU')}/company/leaders/${slug}/` },
					{ hrefLang: 'en-gb', href: `${getBaseUrl('en-GB')}/company/leaders/${slug}/` },
					{ hrefLang: 'en-us', href: `${getBaseUrl('en-US')}/company/leaders/${slug}/` },
					{ hrefLang: 'en-sg', href: `${getBaseUrl('en-SG')}/company/leaders/${slug}/` },
					{ hrefLang: 'x-default', href: `${getBaseUrl()}/company/leaders/${slug}/` }
				]}
				openGraph={{
					title,
					description,
					url: `${baseUrl}/company/leaders/${slug}/`,
					siteName: 'BigCommerce',
					images: seoMetadata?.image?.url
						? [
								{
									url: seoMetadata.image.url,
									width: seoMetadata.image.width,
									height: seoMetadata.image.height,
									alt: 'Bigcommerce logo',
									type: 'image/jpeg'
								}
						  ]
						: undefined,
					locale: getSeoLocale(locale)
				}}
				nofollow={seoMetadata?.nofollow ?? false}
				noindex={Boolean(seoMetadata?.noindex)}
			/>
			<BreadcrumbJsonLd
				itemListElements={[
					{
						position: 1,
						name: 'Homepage',
						item: `${getBaseUrl(locale)}/`
					},
					{
						position: 2,
						name: `${getBaseUrl(locale)}/company/`,
						item: 'About Us'
					},
					{
						position: 3,
						name: `${getBaseUrl(locale)}/company/leaders/`,
						item: 'Leaders'
					},
					{
						position: 3,
						name: `${getBaseUrl(locale)}/company/leaders/${slug}`,
						item: `Leaders - ${fullName}`
					}
				]}
			/>
			{children}
		</>
	)
}
